import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Slide,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';

const GOLD = '#d4af37';

/* ---- slide-up transition for mobile feel ---- */
const SlideUp = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function LoginModal() {
  const {
    loginModalOpen,
    closeLoginModal,
    signInEmail,
    signUpEmail,
    signInGoogle,
    resetPassword,
  } = useAuth();

  const [mode, setMode] = useState<'signin' | 'signup' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const clearForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setError('');
    setSuccess('');
  };

  const switchMode = (m: 'signin' | 'signup' | 'reset') => {
    clearForm();
    setMode(m);
  };

  const handleClose = () => {
    closeLoginModal();
    // Reset state after close animation
    setTimeout(() => {
      clearForm();
      setMode('signin');
    }, 300);
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    if (mode === 'signup' && !displayName) { setError('Please enter your name.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

    try {
      setLoading(true);
      if (mode === 'signin') await signInEmail(email, password);
      else await signUpEmail(email, password, displayName);
      // Modal auto-closes via AuthContext onAuthStateChanged
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/user-not-found') setError('No account found with this email.');
      else if (code === 'auth/wrong-password') setError('Incorrect password.');
      else if (code === 'auth/invalid-credential') setError('Invalid email or password.');
      else if (code === 'auth/email-already-in-use') setError('Email already in use.');
      else if (code === 'auth/weak-password') setError('Password is too weak.');
      else if (code === 'auth/invalid-email') setError('Invalid email address.');
      else setError(err?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    try {
      setLoading(true);
      await signInGoogle();
    } catch (err: any) {
      if (err?.code !== 'auth/popup-closed-by-user') {
        setError('Google sign-in failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!email) { setError('Enter your email address.'); return; }
    try {
      setLoading(true);
      await resetPassword(email);
      setSuccess('Reset link sent! Check your inbox.');
    } catch {
      setError('Could not send reset email.');
    } finally {
      setLoading(false);
    }
  };

  /* -------------- input style -------------- */
  const fieldSx = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: '#f8f7f4',
      fontSize: '0.93rem',
      '& fieldset': { borderColor: '#e8e4dc' },
      '&:hover fieldset': { borderColor: '#d0c9bc' },
      '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.88rem',
      '&.Mui-focused': { color: GOLD },
    },
  };

  return (
    <Dialog
      open={loginModalOpen}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '20px 20px 0 0', sm: '20px' },
          m: { xs: 0, sm: 2 },
          position: { xs: 'fixed', sm: 'relative' },
          bottom: { xs: 0, sm: 'auto' },
          maxHeight: { xs: '88dvh', sm: '90vh' },
          width: { xs: '100%', sm: 'auto' },
          overflow: 'hidden',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          bgcolor: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      {/* ---------- Header strip ---------- */}
      <Box
        sx={{
          background: `linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)`,
          px: 2.5,
          pt: 2.5,
          pb: 2,
          position: 'relative',
          textAlign: 'center',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'rgba(255,255,255,0.6)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Om icon */}
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${GOLD}, #e5c158)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 1.2,
            boxShadow: `0 4px 16px rgba(212,175,55,0.4)`,
          }}
        >
          <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.7rem' }, color: '#1a1a2e', fontWeight: 700, lineHeight: 1, mt: '-2px' }}>
            ॐ
          </Typography>
        </Box>

        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.15rem' }, letterSpacing: 0.3 }}>
          {mode === 'reset' ? 'Reset Password' : mode === 'signin' ? 'Welcome Back' : 'Create Account'}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', mt: 0.3 }}>
          {mode === 'reset'
            ? 'We\'ll send you a reset link'
            : mode === 'signin'
            ? 'Sign in to continue'
            : 'Join our temple community'}
        </Typography>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 3, overflow: 'auto' }}>
        {/* ---------- Toggle pills ---------- */}
        {mode !== 'reset' && (
          <Box
            sx={{
              display: 'flex',
              bgcolor: '#f3f1ec',
              borderRadius: '12px',
              p: '4px',
              mb: 2.5,
            }}
          >
            {(['signin', 'signup'] as const).map((m) => (
              <Box
                key={m}
                onClick={() => !loading && switchMode(m)}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 1,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.86rem',
                  fontWeight: 600,
                  color: mode === m ? '#fff' : '#888',
                  bgcolor: mode === m ? GOLD : 'transparent',
                  boxShadow: mode === m ? '0 2px 8px rgba(212,175,55,0.35)' : 'none',
                  transition: 'all .2s ease',
                  userSelect: 'none',
                }}
              >
                {m === 'signin' ? 'Sign In' : 'Sign Up'}
              </Box>
            ))}
          </Box>
        )}

        {/* ---------- Alerts ---------- */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem', py: 0 }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess('')}
            sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem', py: 0 }}
          >
            {success}
          </Alert>
        )}

        {/* ---------- Google button ---------- */}
        {mode !== 'reset' && (
          <>
            <Button
              fullWidth
              onClick={handleGoogleLogin}
              disabled={loading}
              sx={{
                py: 1.3,
                borderRadius: '12px',
                border: '1.5px solid #e0dcd4',
                bgcolor: '#fff',
                color: '#333',
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                '&:hover': {
                  bgcolor: '#fafaf8',
                  borderColor: '#ccc',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
              }}
            >
              {/* Google "G" colored logo */}
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{ width: 20, height: 20, flexShrink: 0 }}
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </Box>
              Continue with Google
            </Button>

            {/* Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2.2 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#e8e4dc' }} />
              <Typography sx={{ color: '#aaa', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                or
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#e8e4dc' }} />
            </Box>
          </>
        )}

        {/* ---------- Reset back button ---------- */}
        {mode === 'reset' && (
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            onClick={() => switchMode('signin')}
            sx={{
              mb: 1.5,
              textTransform: 'none',
              color: '#888',
              fontWeight: 500,
              fontSize: '0.8rem',
              px: 0,
              '&:hover': { color: GOLD, bgcolor: 'transparent' },
            }}
          >
            Back to Sign In
          </Button>
        )}

        {/* ---------- Form ---------- */}
        <Box component="form" onSubmit={mode === 'reset' ? handleResetPassword : handleEmailAuth}>
          {mode === 'signup' && (
            <TextField
              fullWidth
              label="Full Name"
              placeholder="Your name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: '#bbb', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          <TextField
            fullWidth
            label="Email"
            placeholder="you@example.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: '#bbb', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {mode !== 'reset' && (
            <TextField
              fullWidth
              label="Password"
              placeholder={mode === 'signup' ? 'Min 6 characters' : 'Your password'}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              sx={{ ...fieldSx, mb: mode === 'signin' ? 0.5 : 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#bbb', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#bbb' }}
                    >
                      {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {mode === 'signin' && (
            <Box sx={{ textAlign: 'right', mb: 1.5 }}>
              <Typography
                component="span"
                onClick={() => switchMode('reset')}
                sx={{
                  color: '#999',
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': { color: GOLD },
                }}
              >
                Forgot password?
              </Typography>
            </Box>
          )}

          {/* Primary CTA */}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading}
            disableElevation
            sx={{
              py: 1.35,
              borderRadius: '12px',
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              bgcolor: GOLD,
              color: '#1a1a2e',
              boxShadow: '0 4px 14px rgba(212,175,55,0.35)',
              '&:hover': {
                bgcolor: '#e0c159',
                boxShadow: '0 6px 20px rgba(212,175,55,0.45)',
              },
              '&:disabled': { bgcolor: '#e0dcd4', color: '#999' },
            }}
          >
            {loading ? (
              <CircularProgress size={22} thickness={3} sx={{ color: '#1a1a2e' }} />
            ) : mode === 'reset' ? (
              'Send Reset Link'
            ) : mode === 'signin' ? (
              'Sign In'
            ) : (
              'Create Account'
            )}
          </Button>
        </Box>

        {/* ---------- Footer toggle ---------- */}
        {mode !== 'reset' && (
          <Typography sx={{ textAlign: 'center', mt: 2.5, fontSize: '0.8rem', color: '#999' }}>
            {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            <Box
              component="span"
              onClick={() => !loading && switchMode(mode === 'signin' ? 'signup' : 'signin')}
              sx={{ color: GOLD, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {mode === 'signin' ? 'Sign Up' : 'Sign In'}
            </Box>
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
